/**
 * Tenant Health Matrix Hook
 *
 * Manages tenant health matrix data with filtering, sorting, pagination,
 * and auto-refresh.
 * Phase 3.5-B: Multi-Tenant Visibility
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchTenantHealthMatrix,
  fetchIsolationDistribution,
  fetchVersionDistribution,
} from '../api/tenantHealthApi';
import type {
  TenantHealthMatrixResponse,
  TenantHealthFilter,
  IsolationDistData,
  VersionDistData,
} from '../types/tenantHealth';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export interface UseTenantHealthMatrixOptions {
  refreshInterval?: number;
  fetchOnMount?: boolean;
  pageSize?: number;
}

export interface UseTenantHealthMatrixResult {
  // Health matrix
  matrixData: TenantHealthMatrixResponse | null;
  isLoading: boolean;
  error: Error | null;

  // Distribution charts
  isolationDist: IsolationDistData | null;
  versionDist: VersionDistData | null;

  // Filter state
  filter: TenantHealthFilter;
  setFilter: (filter: TenantHealthFilter) => void;

  // Sort state
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;

  // Pagination
  page: number;
  setPage: (page: number) => void;

  // Actions
  refresh: () => Promise<void>;
  lastUpdated: Date | null;
}

const DEFAULT_FILTER: TenantHealthFilter = {
  status: [],
  isolationType: [],
  search: '',
};

export const useTenantHealthMatrix = (
  authFetch: AuthFetch,
  options: UseTenantHealthMatrixOptions = {}
): UseTenantHealthMatrixResult => {
  const { refreshInterval = 30000, fetchOnMount = true, pageSize = 10 } = options;

  const [matrixData, setMatrixData] = useState<TenantHealthMatrixResponse | null>(null);
  const [isolationDist, setIsolationDist] = useState<IsolationDistData | null>(null);
  const [versionDist, setVersionDist] = useState<VersionDistData | null>(null);
  const [isLoading, setIsLoading] = useState(fetchOnMount !== false);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [filter, setFilter] = useState<TenantHealthFilter>(DEFAULT_FILTER);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);

  const isMounted = useRef(true);
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;
  const hasFetchedRef = useRef(false);

  const fetchData = useCallback(async () => {
    // Only show loading skeleton on initial fetch (no existing data)
    if (!hasFetchedRef.current) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const [matrix, isolation, versions] = await Promise.all([
        fetchTenantHealthMatrix(authFetchRef.current, {
          status: filter.status.length ? filter.status : undefined,
          isolationType: filter.isolationType.length ? filter.isolationType : undefined,
          search: filter.search || undefined,
          page,
          pageSize,
          sortBy,
          sortDirection,
        }),
        fetchIsolationDistribution(authFetchRef.current),
        fetchVersionDistribution(authFetchRef.current),
      ]);

      if (isMounted.current) {
        setMatrixData(matrix);
        setIsolationDist(isolation);
        setVersionDist(versions);
        hasFetchedRef.current = true;
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err instanceof Error ? err : new Error('Failed to fetch tenant health data'));
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [filter, page, pageSize, sortBy, sortDirection]);

  const onSort = useCallback((column: string) => {
    setSortBy((prev) => {
      if (prev === column) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return column;
    });
    setPage(1);
  }, []);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // Initial fetch
  useEffect(() => {
    if (fetchOnMount) {
      fetchData();
    }
  }, [fetchOnMount, fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (refreshInterval <= 0) return;
    const id = setInterval(fetchData, refreshInterval);
    return () => clearInterval(id);
  }, [refreshInterval, fetchData]);

  // Cleanup
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    matrixData,
    isLoading,
    error,
    isolationDist,
    versionDist,
    filter,
    setFilter,
    sortBy,
    sortDirection,
    onSort,
    page,
    setPage,
    refresh: fetchData,
    lastUpdated,
  };
};

export default useTenantHealthMatrix;
